import {isFeatureEnabled} from '@github-ui/feature-flags'
import {reportTraceData} from '@github-ui/internal-api-insights'
import type {createSafeIndexedDbPersister} from '@github-ui/safe-indexed-db-storage'
import type {SendAnalyticsEventFunction} from '@github-ui/use-analytics'
import type {Sink} from 'relay-runtime/lib/network/RelayObservable'

type Storage = ReturnType<typeof createSafeIndexedDbPersister>

const PREHEAT_NOOP_EVENT = 'offline_cache.preheat_noop'

interface StoragePersisterOptions {
  /** The storage client used for setting and retrieving items from cache.
   * For SSR pass in `undefined`.
   */
  storage: Storage | undefined | null
  /**
   * The max-allowed age of the cache in milliseconds.
   * If a persisted cache is found that is older than this
   * time, it will be discarded
   */
  maxAgeMs: number
  /**
   * Prefix to be used for storage key.
   * Storage key is a combination of prefix and query hash in a form of `prefix-queryHash`.
   */
  prefix: string
  /**
   * Function to send custom analytics events (that aren't handled by the storage client)
   */
  sendAnalyticsEvent: SendAnalyticsEventFunction
}
type CacheKey = string

declare const InvalidBrand: unique symbol
type Invalid = {
  readonly [InvalidBrand]: true
}
const INVALID: Invalid = {} as Invalid

/**
 * Generates a cache key from query name and variables
 */
function generateCacheKey(
  prefix: string,
  queryName: string,
  variables: Record<string, unknown>,
  defaultValues?: Record<string, unknown>,
): CacheKey {
  const variablesWithDefaults = {
    ...defaultValues,
    ...Object.fromEntries(Object.entries(variables).filter(([, v]) => v !== undefined)),
  }
  const sortedVariableKeys = Object.keys(variablesWithDefaults).sort()
  const sortedVariables: Record<string, unknown> = {}
  for (const key of sortedVariableKeys) {
    const value = variablesWithDefaults[key]
    if (value !== undefined && value !== null) {
      sortedVariables[key] = value
    }
  }
  return `${prefix}-${queryName}#${JSON.stringify(sortedVariables)}`
}

export type QueryCache<T> = ReturnType<typeof createQueryCache<T>>

export function createQueryCache<T>({storage, prefix, maxAgeMs, sendAnalyticsEvent}: StoragePersisterOptions) {
  const isClientSideCachingAnalyticsEnabled = isFeatureEnabled('issues_react_extended_preheat_analytics')

  async function retrieveQuery(
    queryId: string,
    queryName: string,
    variables: Record<string, unknown>,
    attributes?: {[key: string]: unknown},
    defaultValues?: Record<string, unknown>,
  ): Promise<T | undefined | Invalid> {
    if (!storage) {
      return undefined
    }
    const cacheKey = generateCacheKey(prefix, queryName, variables, defaultValues)
    try {
      const persistedQuery = await storage.getItem(cacheKey, attributes)
      if (persistedQuery && persistedQuery.queryHash !== queryId) {
        // The persisted query has changed and so the cached version is no longer valid
        return INVALID
      }
      return persistedQuery?.state.data as T | undefined
    } catch {
      return undefined
    }
  }

  async function evictQuery(cacheKey: CacheKey) {
    if (!storage) {
      return
    }
    try {
      await storage.removeItem(cacheKey)
    } catch {
      // eslint-disable-next-line no-console
      console.warn(`Failed to evict query for key: ${cacheKey}`)
    }
  }

  async function tryCacheFetchResult(
    queryId: string,
    queryName: string,
    variables: Record<string, unknown>,
    fetchResult: T | null,
    ttl: number,
    attributes?: {[key: string]: unknown},
    defaultValues?: Record<string, unknown>,
    preheatSource?: string,
  ) {
    if (!storage) {
      return undefined
    }
    const cacheKey = generateCacheKey(prefix, queryName, variables, defaultValues)
    try {
      await storage.setItem(
        cacheKey,
        {
          queryHash: queryId,
          queryKey: Object.entries(variables).flat(),
          state: {data: fetchResult, dataUpdatedAt: Date.now(), ttl, preheatSource},
        },
        undefined,
        undefined,
        attributes,
      )
    } catch {
      // eslint-disable-next-line no-console
      console.warn(`Failed to persist query for key: ${cacheKey}`)
    }
  }

  async function persisterFn(
    queryFn: (isCacheRefresh?: boolean) => Promise<T>,
    queryId: string,
    queryName: string,
    variables: Record<string, unknown>,
    observer: Sink<T>,
    ttl?: number,
    attributes?: {[key: string]: unknown},
    defaultValues?: Record<string, unknown>,
  ): Promise<T | undefined> {
    const cachedResult = await retrieveQuery(queryId, queryName, variables, attributes, defaultValues)
    const cacheKey = generateCacheKey(prefix, queryName, variables, defaultValues)
    const hasCachedResult = cachedResult !== undefined && cachedResult !== INVALID
    const preheatSource = attributes?.preheat_source as string | undefined
    const preHeated = !!preheatSource
    const skipCache = !!(attributes?.skip_cache as boolean | undefined)

    let cachedResultString: string | null = null
    let queryResultString: string | null = null
    if (hasCachedResult) {
      if (typeof cachedResult === 'object' && cachedResult !== null && 'data' in cachedResult) {
        cachedResultString = JSON.stringify((cachedResult as {[key: string]: unknown}).data)
      }
      reportTraceData({
        __trace: {
          query_name: attributes?.query_name as string | undefined,
          query_id: queryId,
          query_variables: variables,
          duration_ms: 1,
          cache_result: 'hit',
        },
      })
      if (!preHeated && !skipCache) {
        observer.next(cachedResult as T)
      }
    }

    if (preHeated && hasCachedResult) {
      // If preHeated is set and we have a cached result, don't return anything
      if (isClientSideCachingAnalyticsEnabled && preheatSource) {
        sendAnalyticsEvent(PREHEAT_NOOP_EVENT, cacheKey, {preheatSource})
      }
      return
    }

    let fetchResult: T
    try {
      fetchResult = await queryFn(hasCachedResult)
    } catch (error) {
      // If queryFn throws and we have cached data, emit stale cache warning
      // and return the cached result to prevent UI breakage
      if (hasCachedResult) {
        dispatchStaleCacheWarning([{message: error instanceof Error ? error.message : 'Unknown error'}])
        return cachedResult as T
      }
      // If no cached data, re-throw the error
      throw error
    }

    // Emit stale cache warning if we served cached data but server returned errors
    if (hasCachedResult && hasErrors(fetchResult)) {
      dispatchStaleCacheWarning((fetchResult as {errors: unknown[]}).errors)
    }

    const clearCache = shouldClearCache<T>(fetchResult, cachedResult)
    const writeToCache = shouldWriteToCache<T>(fetchResult)

    if (storage && clearCache) {
      evictQuery(cacheKey)
    } else if (writeToCache && storage) {
      // Only cache successful results
      if (typeof fetchResult === 'object' && fetchResult !== null && 'data' in fetchResult) {
        queryResultString = JSON.stringify((fetchResult as {[key: string]: unknown}).data)
      }

      const updateType = getUpdateType(!!cachedResult, cachedResultString, queryResultString, preHeated, skipCache)
      tryCacheFetchResult(
        queryId,
        queryName,
        variables,
        fetchResult,
        ttl ?? maxAgeMs,
        {...attributes, update_type: updateType},
        defaultValues,
        preheatSource,
      )
    }

    // fetchResult will be defined if no error was thrown
    return fetchResult
  }

  return {persisterFn, retrieveQuery, tryCacheFetchResult}
}

function getUpdateType(
  hasCachedResult: boolean,
  cachedResult: string | null,
  queryResult: string | null,
  preHeated: boolean | undefined,
  skipCache: boolean | undefined,
) {
  if (skipCache) {
    return 'force_refresh'
  }
  if (!hasCachedResult) {
    return preHeated ? 'pre_heated' : 'new'
  }
  return cachedResult !== queryResult ? 'changed' : 'unchanged'
}

function shouldClearCache<T>(fetchResult: T, cachedResult: T | undefined | Invalid): boolean {
  if (!cachedResult) return false
  if (cachedResult === INVALID) return true
  if (!fetchResult) return false

  return hasErrors(fetchResult)
}

function hasErrors<T>(result: T): boolean {
  return (
    typeof result === 'object' &&
    result !== null &&
    'errors' in result &&
    Array.isArray(result.errors) &&
    result.errors.length > 0
  )
}

function dispatchStaleCacheWarning(errors: unknown[]) {
  if (typeof window === 'undefined') return

  window.dispatchEvent(
    new CustomEvent('stale-cache-warning', {
      detail: {
        errors,
      },
    }),
  )
}

function shouldWriteToCache<T>(fetchResult: T): boolean {
  if (!fetchResult) return false

  if (typeof fetchResult === 'object') {
    if ('errors' in fetchResult && Array.isArray(fetchResult.errors) && fetchResult.errors.length > 0) {
      return false
    }
  }
  return true
}
